import { createZoomImageWheel, ZoomImageWheelState, ZoomImageWheelStateUpdate } from '@zoom-image/core'
import { VisibilityElement } from './visibility'

interface Selectors {
  readonly zoomContainer: string;
  readonly container: string;
  readonly buttons: string;
  readonly globalTriggers: string;
  readonly close: string;
  readonly scrollValue: string;
}

interface ScrollBehavior {
  readonly default: ScrollIntoViewOptions;
  readonly none: ScrollIntoViewOptions;
}

interface GalleryActions {
  readonly next: 'next';
  readonly prev: 'prev';
}

type GalleryAction = GalleryActions[keyof GalleryActions];

interface StateChangePayload {
  index?: string;
  action?: GalleryAction;
  scrollBehavior?: keyof ScrollBehavior;
  images?: NodeListOf<Element>;
}

interface ZoomState {
  currentZoom: number;
  [key: string]: unknown;
}

// Missing type definitions for @zoom-image/core
type StoreListener<TState> = (arg: {
  state: TState;
  updatedProperties: Partial<TState>;
}) => void;

// Missing type definitions for @zoom-image/core
interface ZoomContainerReference {
  cleanup(): void;
  subscribe: (listener: StoreListener<ZoomImageWheelState>) => () => void;
  setState: (newState: ZoomImageWheelStateUpdate) => void;
  getState: () => ZoomImageWheelState;
}

/**
 * LightboxGallery - Custom Element
 * Extended from VisibilityElement.
 *
 * Usage:
 * 1. Wrap modal elements with <x-lighthouse-gallery>
 * 2. Set data-in-view attribute to the index of the element in view
 * 3. Add data-role="lighthouse-trigger" to trigger elements
 */
class LightBoxElement extends VisibilityElement {
  private readonly selectors: Selectors
  private readonly scrollBehavior: ScrollBehavior
  private readonly galleryActions: GalleryActions
  private readonly zoomContainerReferences: ZoomContainerReference[]
  private readonly galleryImages: NodeListOf<Element>

  constructor() {
    super()

    this.selectors = {
      zoomContainer: 'div[data-role="zoom_image_container"]',
      container: 'div[data-role="gallery_image_container"]',
      buttons: 'button[data-role="gallery-button"]',
      globalTriggers: 'div[data-role="lightbox-trigger"]',
      close: '[data-role="close"]',
      scrollValue: '[data-role="scroll-value"]'
    } as const

    this.scrollBehavior = {
      default: { behavior: 'smooth', inline: 'start' },
      none: {}
    } as const

    this.galleryActions = {
      next: 'next',
      prev: 'prev'
    } as const

    this.zoomContainerReferences = []
    this.galleryImages = this.querySelectorAll(this.selectors.container)

    this.handleStateChange()
  }

  handleInView = (index: string): void => {
    this.dataset.inView = index
  }

  /**
   * Handles state changes in the gallery
   * @param payload - State change configuration
   * @returns The new index after state change
   */
  private handleStateChange(payload?: StateChangePayload): number {
    const index = payload?.index ?? this.dataset.inView
    const images = this.galleryImages
    const parsedIndex = parseInt(index ?? '0', 10)

    if (this.zoomContainerReferences.length) {
      this.zoomContainerReferences[parsedIndex]?.setState({ currentZoom: 0 })
    }

    const newIndex = this.galleryActionsMap(payload?.action, parsedIndex, images.length)
    this.handleInView(newIndex.toString())

    const behavior = payload?.scrollBehavior ?? 'none'
    images[newIndex]?.scrollIntoView(this.scrollBehavior[behavior])
    this.handleZoomValue(newIndex, images)

    return newIndex
  }

  /**
   * Updates zoom value display for mobile users
   */
  private handleZoomValue(index: number, images: NodeListOf<Element>): void {
    const container = images[index]?.querySelector<HTMLElement>(this.selectors.zoomContainer)
    const currentZoom = container?.dataset.mobileZoom
    const scrollElement = document.querySelector<HTMLElement>(this.selectors.scrollValue)
    if (scrollElement && currentZoom) {
      scrollElement.innerHTML = `(${currentZoom}%)`
    }
  }

  /**
   * Handles gallery wrap-around behavior
   */
  private handleGalleryWrap(currentIndex: number, images: NodeListOf<Element>): void {
    const currentImage = images[currentIndex] as HTMLElement
    const wrap = currentImage?.dataset.type

    if (wrap) {
      const resetIndex = currentIndex >= images.length - 1 ? 1 : images.length - 2
      setTimeout(() => {
        (images[resetIndex] as HTMLElement)?.scrollIntoView({})
        this.handleInView(resetIndex.toString())
        this.handleZoomValue(resetIndex, images)
      }, 1000)
    }
  }

  /**
   * Handles gallery button clicks
   */
  private handleButtons = (e: Event): void => {
    const button = e.target as HTMLButtonElement
    const action = button.dataset.action as GalleryAction
    const images = this.galleryImages
    const newIndex = this.handleStateChange({ action, scrollBehavior: 'default', images })
    this.handleGalleryWrap(newIndex, images)
  }

  /**
   * Handles global trigger clicks
   */
  private handleGlobalTrigger = (e: Event): void => {
    this.handleToggle()
    const image = e.currentTarget as HTMLElement
    const index = image.dataset.index
    if (index) {
      this.handleStateChange({ index, scrollBehavior: 'none' })
    }
  }

  /**
   * Closes the lightbox
   */
  private handleClose = (): void => {
    this.handleHide()
  }

  /**
   * Initializes zoom functionality for images
   */
  private handleImageZoom(): void {
    this.querySelectorAll<HTMLElement>(this.selectors.zoomContainer).forEach((el) => {
      const result = createZoomImageWheel(el)
      this.zoomContainerReferences.push(result)
      result.subscribe(({ state }: { state: ZoomState }) => {
        this.imageZoomUpdate(state, el)
      })
    })
  }

  /**
   * Updates zoom state for selected image
   */
  private imageZoomUpdate(state: ZoomState, element: HTMLElement): void {
    try {
      const scroll = Math.round(state?.currentZoom * 100)
      const scrollElement = document.querySelector<HTMLElement>(this.selectors.scrollValue)
      if (scrollElement) {
        element.dataset.mobileZoom = scroll.toString()
        scrollElement.innerHTML = `(${scroll}%)`
      }
    } catch (err) {
      console.error('Error updating image zoom:', err)
    }
  }

  /**
   * Maps gallery actions to index changes
   */
  private galleryActionsMap(
    action: GalleryAction | undefined,
    currentValue: number,
    totalImages: number
  ): number {
    switch (action) {
      case this.galleryActions.next:
        return currentValue < totalImages - 1 ? currentValue + 1 : currentValue
      case this.galleryActions.prev:
        return currentValue > 0 ? currentValue - 1 : currentValue
      default:
        return currentValue
    }
  }

  /**
   * Adds event listeners
   */
  addEventListeners(): void {
    super.addEventListeners()

    this.querySelectorAll<HTMLElement>(this.selectors.buttons)
      .forEach(el => el.addEventListener('click', this.handleButtons))

    document.querySelectorAll<HTMLElement>(this.selectors.globalTriggers)
      .forEach(el => el.addEventListener('click', this.handleGlobalTrigger))

    document.querySelectorAll<HTMLElement>(this.selectors.close)
      .forEach(el => el.addEventListener('click', this.handleClose))
  }

  /**
   * Removes event listeners
   */
  removeEventListeners(): void {
    super.removeEventListeners()

    this.querySelectorAll<HTMLElement>(this.selectors.buttons)
      .forEach(el => el.removeEventListener('click', this.handleButtons))

    document.querySelectorAll<HTMLElement>(this.selectors.globalTriggers)
      .forEach(el => el.removeEventListener('click', this.handleGlobalTrigger))

    document.querySelectorAll<HTMLElement>(this.selectors.close)
      .forEach(el => el.removeEventListener('click', this.handleClose))
  }

  connectedCallback(): void {
    this.handleImageZoom()
    this.addEventListeners()
  }

  disconnectedCallback(): void {
    this.removeEventListeners()
  }
}

export { LightBoxElement }
